import { AmpFlagSet } from './amplitude';
import { logEvent, logException } from './logging';
import { isUnified } from './helpers';
import { LiveChatState } from './hooks/useLiveChatState';

export const INACTIVE_LIVE_CHAT_TIMEOUT_MINUTES = 5;
const LIVE_CHAT_CLOSED_IN_TAB_CODE = 200; // See https://developer.genesys.cloud/commdigital/digital/webmessaging/websocketapi#multiple-connections
const RESUME_FROM_APP_TIMEOUT_MILLISECONDS = 1000;

export const LIVE_CHAT_ERROR_JWT_UNDEFINED = 'Genesys JWT undefined';
export const LIVE_CHAT_ERROR_IDENTITY_NOT_VERIFIED =
    'Genesys Identity Not Verified';

export enum LiveChatConnectionStatus {
    disconnected,
    connecting,
    serviceConnected,
    agentConnected,
}

export enum LiveChatConnectionType {
    connect,
    reconnect,
}

export interface LiveChatPatientData {
    firstName: string;
    EPI: string;
    epicRegion: string;
    correlationId: string;
    conversationId: string;
    isMobileBrowser: boolean;
}

// eslint-disable-next-line
export interface LiveChatServiceEvent {}
export class OnLiveChatOpeningEvent implements LiveChatServiceEvent {}
export class OnLiveChatOpenedEvent implements LiveChatServiceEvent {}
export class OnLiveChatClosedEvent implements LiveChatServiceEvent {}
export class OnLiveChatClosedInTabEvent implements LiveChatServiceEvent {}
export class OnLiveChatAgentConnectedEvent implements LiveChatServiceEvent {}
export class OnLiveChatActiveEvent implements LiveChatServiceEvent {}
export class OnLiveChatInactiveEvent implements LiveChatServiceEvent {}
export class OnLiveChatMessageEvent implements LiveChatServiceEvent {
    isUserMessage: boolean;
    isSystemMessage: boolean;
    message: any;

    constructor(
        isUserMessage: boolean,
        isSystemMessage: boolean,
        message: any
    ) {
        this.isUserMessage = isUserMessage;
        this.isSystemMessage = isSystemMessage;
        this.message = message;
    }
}
export class OnLiveChatErrorEvent implements LiveChatServiceEvent {
    error: string;
    connectionStatus: LiveChatConnectionStatus;

    constructor(error: string, connectionStatus: LiveChatConnectionStatus) {
        this.error = error;
        this.connectionStatus = connectionStatus;
    }
}

export type EventsListener = (event: LiveChatServiceEvent) => void;

export interface EventsListenerDisposer {
    dispose: () => void;
}

export interface EventsStream {
    listen: (listener: EventsListener) => EventsListenerDisposer;
}

let nextListenerId = 1;

export const isAgentMessage = (message: any) => {
    return message?.body?.direction == 'Outbound';
};

export const isCurrentTabUserMessage = (message: any, isFocused: boolean) => {
    const text = message?.body?.text;
    if (!text) {
        return false;
    }
    const isUser = message?.body?.direction == 'Inbound';
    return isUser && isFocused && !text.includes('FROM_GRACE');
};

export class LiveChatService {
    featureFlags: AmpFlagSet;
    deploymentId: string | null;
    token: string | null;
    connectionType: LiveChatConnectionType;
    client: LiveChatPatientData | null;
    jwt: string;
    ws: WebSocket | null;
    eventsListeners: Map<number, EventsListener>;
    eventsStream: EventsStream;
    connectionStatus: LiveChatConnectionStatus;
    activeTimeoutId: NodeJS.Timeout | undefined;
    currentTabClosed: boolean;
    url: string | undefined;
    sendEvent: (name: string, value: any) => void;
    state: LiveChatState;
    appRecentlySuspended: boolean;
    webSocketClosedFromApp: boolean;
    resumeFromAppTimeoutId: NodeJS.Timeout | undefined;
    refOnVisibilityStateChangedInApp:
        | ((this: Document, ev: Event) => any)
        | undefined;

    constructor() {
        this.featureFlags = {};
        this.deploymentId = null;
        this.token = null;
        this.connectionType = LiveChatConnectionType.connect; // default
        this.client = null;
        this.jwt = '';
        this.sendEvent = (_name: string, _value: any) => undefined;
        this.ws = null;
        this.activeTimeoutId = undefined;
        this.currentTabClosed = false;
        this.connectionStatus = LiveChatConnectionStatus.disconnected;
        this.appRecentlySuspended = false;
        this.webSocketClosedFromApp = false;

        this.eventsListeners = new Map<number, EventsListener>();
        this.eventsStream = {
            listen: (listener: EventsListener) => {
                const listenerId = nextListenerId++;
                this.eventsListeners.set(listenerId, listener);

                return {
                    dispose: () => this.eventsListeners.delete(listenerId),
                } as EventsListenerDisposer;
            },
        } as EventsStream;
        this.state = LiveChatState.notStarted;
    }

    initialize(
        featureFlags: AmpFlagSet,
        deploymentId: string,
        token: string,
        connectionType: LiveChatConnectionType,
        jwt: string,
        sendEvent: (name: string, value: any) => void,
        client: LiveChatPatientData
    ) {
        if (this.connectionStatus != LiveChatConnectionStatus.disconnected) {
            return;
        }

        this.notifyListeners(new OnLiveChatOpeningEvent());
        this.featureFlags = featureFlags;
        this.deploymentId = deploymentId;
        this.token = token;
        this.connectionType = connectionType;
        this.jwt = jwt;
        this.sendEvent = sendEvent;
        this.client = client;
        this.url = `wss://webmessaging.usw2.pure.cloud/v1?deploymentId=${deploymentId}`;
        this.appRecentlySuspended = false;
        this.webSocketClosedFromApp = false;
        if (isUnified()) {
            this.refOnVisibilityStateChangedInApp =
                this._onVisibilityStateChangedInApp.bind(this);
            document.addEventListener(
                'visibilitychange',
                this.refOnVisibilityStateChangedInApp,
                false
            );
        }

        this._setupWebSocket();
    }

    // If iOS users background live chat, when they go back a few hundred ms after the web socket closes
    // Try to detect and rerun websocket setup in this case
    _onVisibilityStateChangedInApp = () => {
        const visibilityState = document.visibilityState;
        if (visibilityState !== 'visible') {
            clearTimeout(this.resumeFromAppTimeoutId);
            this.appRecentlySuspended = true;
            return;
        }

        if (document.visibilityState === 'visible') {
            const reconnectWebSocket = () => {
                this.appRecentlySuspended = false;
                if (this.webSocketClosedFromApp) {
                    this._setupWebSocket();
                }
            };
            this.resumeFromAppTimeoutId = setTimeout(
                reconnectWebSocket.bind(this),
                RESUME_FROM_APP_TIMEOUT_MILLISECONDS
            );
        }
    };

    _setupWebSocket() {
        if (!this.url) {
            logException('LiveChatService - Undefined websocket url', {
                exception: Error('Undefined url'),
            });
            this.setError('Undefined websocket url');
            return;
        }

        if (this.connectionStatus === LiveChatConnectionStatus.connecting) {
            return;
        }

        this.connectionStatus = LiveChatConnectionStatus.connecting;

        this.ws = new WebSocket(this.url);
        this.ws.onopen = () => {
            this.onopen();
        };
        this.ws.onmessage = (e) => {
            this.onmessage(e);
        };
        this.ws.onclose = () => {
            if (this.appRecentlySuspended) {
                this.webSocketClosedFromApp = true;
                return;
            }
            this.onclose();
        };
        this.ws.onerror = (e: any) => {
            if (this.appRecentlySuspended) {
                return;
            }
            logException('LiveChatService - Websocket connection error', {
                exception: e,
            });
            this.notifyListeners(
                new OnLiveChatErrorEvent(e, this.connectionStatus)
            );
        };
    }

    setError(error: string) {
        this._close();
        this.notifyListeners(
            new OnLiveChatErrorEvent(error, this.connectionStatus)
        );
    }

    onconnection() {
        if (this.state === LiveChatState.stopped) {
            return;
        }
        const isNewConnection =
            this.connectionType === LiveChatConnectionType.connect;
        const connectionText = isNewConnection
            ? 'FROM_GRACE: User has connected to live chat'
            : 'FROM_GRACE: User has reconnected to live chat';
        const customDataPayload = {
            action: 'onMessage',
            token: this.token,
            message: {
                type: 'Text',
                text: connectionText,
                channel: {
                    metadata: {
                        customAttributes: {
                            firstName: this.client?.firstName,
                            EPI: this.client?.EPI,
                            GBEpicLocationName: this.client?.epicRegion,
                            correlationId: this.client?.correlationId,
                            conversationId: this.client?.conversationId,
                            isMobileBrowser: this.client?.isMobileBrowser,
                        },
                    },
                },
            },
        };
        this.send(customDataPayload);
    }

    onopen() {
        logEvent({ name: 'LiveChatOpened' });
        this.notifyListeners(new OnLiveChatOpenedEvent());

        const initPayload = {
            action: 'configureAuthenticatedSession',
            deploymentId: this.deploymentId,
            token: this.token,
            data: {
                code: this.jwt,
            },
        };
        this.send(initPayload);
    }

    onmessage(event: any) {
        try {
            const messageReceived = JSON.parse(event.data);

            if (this.connectionStatus == LiveChatConnectionStatus.connecting) {
                if (messageReceived?.body?.connected) {
                    logEvent({ name: 'LiveChatServiceConnected' });
                    this.connectionStatus =
                        LiveChatConnectionStatus.serviceConnected;
                    this.onconnection();
                } else {
                    logException(
                        'LiveChatService: Received error message while connecting',
                        {
                            exception: new Error(
                                `${messageReceived.body} (${messageReceived.code})`
                            ),
                        }
                    );
                    this.notifyListeners(
                        new OnLiveChatErrorEvent(
                            `Error message while connecting. ${messageReceived.body} (${messageReceived.code})`,
                            this.connectionStatus
                        )
                    );
                }
                return;
            }

            const isTypingMessage =
                messageReceived?.class === 'StructuredMessage' &&
                messageReceived?.type === 'message' &&
                messageReceived?.body?.type === 'Event' &&
                messageReceived?.body?.events.find(
                    (e: any) => e.eventType === 'Typing'
                ) !== undefined;
            const isUserMessage =
                messageReceived?.class === 'StructuredMessage' &&
                messageReceived?.type === 'message' &&
                messageReceived?.body?.type === 'Text';
            const isSystemMessage =
                messageReceived?.body?.text &&
                messageReceived?.body?.text?.length >= 10 &&
                messageReceived?.body?.text.substring(0, 10) === 'FROM_GRACE';
            const isConnectionClosedMessage =
                messageReceived?.class === 'ConnectionClosedEvent' &&
                messageReceived?.type === 'message';
            const isEndConversationMessage =
                messageReceived?.body?.events?.length &&
                messageReceived?.body?.events[0]?.presence?.type ===
                    'Disconnect';

            if (
                isTypingMessage &&
                this.connectionStatus != LiveChatConnectionStatus.agentConnected
            ) {
                logEvent({ name: 'LiveChatAgentConnected' });
                this.connectionStatus = LiveChatConnectionStatus.agentConnected;
                this.notifyListeners(new OnLiveChatAgentConnectedEvent());
                this.resetIdleState();
            }

            if (
                isConnectionClosedMessage &&
                messageReceived?.code === LIVE_CHAT_CLOSED_IN_TAB_CODE
            ) {
                this.currentTabClosed = true;
                return;
            }

            if (
                messageReceived.body.direction === 'Outbound' &&
                isEndConversationMessage
            ) {
                logEvent({ name: 'LiveChatAgentClose' });
                this.agentClose();
                return;
            }

            if (
                isUserMessage &&
                messageReceived.body.direction === 'Outbound' &&
                this.connectionStatus != LiveChatConnectionStatus.agentConnected
            ) {
                logEvent({ name: 'LiveChatAgentConnected' });
                this.connectionStatus = LiveChatConnectionStatus.agentConnected;
                this.notifyListeners(new OnLiveChatAgentConnectedEvent());
                this.resetIdleState();
            }

            if (
                isUserMessage &&
                messageReceived?.body?.text &&
                messageReceived?.body?.direction
            ) {
                const text = messageReceived.body.text;
                const isAgent = isAgentMessage(messageReceived);
                const isCurrentTabUser = isCurrentTabUserMessage(
                    messageReceived,
                    document.hasFocus()
                );
                if (isAgent || isCurrentTabUser) {
                    this.sendEvent('liveChatMessage', {
                        from: isAgent ? 'agent' : 'user',
                        text,
                        id: messageReceived.body.id,
                    });
                }
            }

            this.notifyListeners(
                new OnLiveChatMessageEvent(
                    isUserMessage,
                    isSystemMessage,
                    messageReceived
                )
            );
        } catch (error: any) {
            logException('LiveChatService - Fail to handle incoming message', {
                exception: error,
            });
        }
    }

    onclose() {
        logEvent({ name: 'LiveChatClosed' });
        this.connectionStatus = LiveChatConnectionStatus.disconnected;
        if (this.currentTabClosed) {
            this.notifyListeners(new OnLiveChatClosedInTabEvent());
        } else {
            this.notifyListeners(new OnLiveChatClosedEvent());
        }

        this._removeEventListeners();

        // eslint-disable-next-line no-restricted-globals
        parent.window.postMessage('liveChatEnded', '*');
    }

    _removeEventListeners() {
        if (isUnified() && this.refOnVisibilityStateChangedInApp) {
            document.removeEventListener(
                'visibilitychange',
                this.refOnVisibilityStateChangedInApp
            );
            this.refOnVisibilityStateChangedInApp = undefined;
        }
    }

    sendMessage(message: string) {
        if (!this.ws) {
            return;
        }

        const messagePayload = {
            action: 'onMessage',
            token: this.token,
            message: {
                type: 'Text',
                text: message,
            },
        };

        this.resetIdleState();
        this.send(messagePayload);
    }

    send(payload: object) {
        if (!this.ws) {
            return;
        }
        this.ws.send(JSON.stringify(payload));
    }

    _close() {
        if (this.activeTimeoutId !== undefined) {
            clearTimeout(this.activeTimeoutId);
            this.activeTimeoutId = undefined;
        }

        if (this.ws && this.ws.close) {
            this.ws.close();
        }

        this.deploymentId = null;
        this.token = null;
        this.jwt = '';
        this.ws = null;
        this.connectionStatus = LiveChatConnectionStatus.disconnected;
        // eslint-disable-next-line no-restricted-globals
        parent.window.postMessage('liveChatEnded', '*');
    }

    userClose() {
        const customDataPayload = {
            action: 'onMessage',
            token: this.token,
            message: {
                type: 'Text',
                text: 'FROM_GRACE: User ended the conversation',
            },
        };
        this.send(customDataPayload);
        this._close();
    }

    agentClose() {
        this._close();
    }

    keepAlive() {
        logEvent({ name: 'LiveChatKeepAlive' });

        const customDataPayload = {
            action: 'onMessage',
            token: this.token,
            message: {
                type: 'Text',
                text: 'FROM_GRACE: User is waiting',
            },
        };

        this.resetIdleState();
        this.send(customDataPayload);
    }

    resetIdleState() {
        if (this.activeTimeoutId !== undefined) {
            clearTimeout(this.activeTimeoutId);
        }

        this.activeTimeoutId = setTimeout(() => {
            logEvent({ name: 'LiveChatTimeout' });
            this.activeTimeoutId = undefined;
            this.notifyListeners(new OnLiveChatInactiveEvent());
        }, INACTIVE_LIVE_CHAT_TIMEOUT_MINUTES * 60000);

        this.notifyListeners(new OnLiveChatActiveEvent());
    }

    notifyListeners(event: LiveChatServiceEvent) {
        this.eventsListeners.forEach((listener, _) => listener(event));
    }
}

const liveChatService = new LiveChatService();
export function getLiveChatService() {
    return liveChatService;
}
